import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logClientError } from "@/lib/error-logger";
import {
  registerRecovery,
  initialRecoveryState,
  type RecoveryState,
} from "@/lib/recovery-policy";
import { isJavaScriptAssetLoadError } from "@/lib/asset-load-error";
import { ownBundleVersion } from "@/lib/stale-bundle";
import { isOutdated } from "@/lib/version-banner";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
  staleBundle: boolean;
}

function isDomSyncError(error: Error): boolean {
  const message = error.message.toLowerCase();
  const domSyncPatterns = [
    "node was not found",
    "the node to be removed is not a child",
    "failed to execute 'removechild'",
    "failed to execute 'insertbefore'",
    "failed to execute 'appendchild'",
    "failed to execute 'replacechild'",
  ];
  return domSyncPatterns.some((pattern) => message.includes(pattern));
}

export class ErrorBoundary extends Component<Props, State> {
  // Loop-detection lives in instance fields, not React state: React's static
  // getDerivedStateFromError can't read state, and state updates batch/lag
  // during a tight throw-recover loop, which is exactly when we must count
  // reliably.
  private recovery: RecoveryState = initialRecoveryState;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, staleBundle: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Browser translate / portal teardown can desync React from the DOM.
    // Tentatively recover; componentDidCatch enforces the attempt cap and
    // flips us back to the error UI if this turns out to be a real loop.
    if (isDomSyncError(error)) {
      return { hasError: false, error: null, errorInfo: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }): void {
    const domSyncError = isDomSyncError(error);
    if (domSyncError) {
      const { next, stop } = registerRecovery(this.recovery, Date.now());
      this.recovery = next;

      logClientError({
        errorCode: "UI-DOM-0001",
        message: error.message,
        stack: error.stack,
        severity: stop ? "critical" : "warning",
        metadata: JSON.stringify({
          componentStack: errorInfo.componentStack,
          autoRecovered: !stop,
          recoveryCount: next.count,
        }),
      });

      if (stop) {
        // Too many DOM-sync errors in a short window: this is a deterministic
        // render loop, not a transient race. Show the error UI so the tab
        // stops thrashing.
        console.error("[ErrorBoundary] DOM sync recovery limit reached; showing error UI:", error.message);
        this.setState({ hasError: true, error, errorInfo });
      } else {
        console.warn("[ErrorBoundary] DOM sync error; auto-recovering:", error.message);
      }
      return;
    }

    if (isJavaScriptAssetLoadError(error)) {
      void this.handleJavaScriptAssetLoadError(error, errorInfo);
      return;
    }

    this.setState({ errorInfo });

    logClientError({
      errorCode: "UI-0003",
      message: error.message,
      stack: error.stack,
      severity: "critical",
      metadata: JSON.stringify({
        componentStack: errorInfo.componentStack,
      }),
    });
  }

  private handleJavaScriptAssetLoadError = async (
    error: Error,
    errorInfo: { componentStack: string },
  ): Promise<void> => {
    const ownVersion = ownBundleVersion();
    let serverVersion: string | null = null;

    try {
      const response = await fetch("/api/version", { cache: "no-store" });
      if (response.ok) {
        const body = await response.json();
        serverVersion = typeof body?.version === "string" ? body.version : null;
      }
    } catch {
      // If the version cannot be confirmed, retain critical handling below.
    }

    const staleBundle = isOutdated(ownVersion, serverVersion);
    this.setState({ errorInfo, staleBundle });

    logClientError({
      errorCode: staleBundle ? "UI-STALE-0001" : "UI-0003",
      message: error.message,
      stack: error.stack,
      severity: staleBundle ? "warning" : "critical",
      metadata: JSON.stringify({
        componentStack: errorInfo.componentStack,
        assetLoadError: true,
        ownVersion,
        serverVersion,
        staleBundle,
      }),
    });
  };

  handleReset = (): void => {
    this.recovery = initialRecoveryState;
    this.setState({ hasError: false, error: null, errorInfo: null, staleBundle: false });
    window.location.reload();
  };

  handleGoBack = (): void => {
    this.recovery = initialRecoveryState;
    this.setState({ hasError: false, error: null, errorInfo: null, staleBundle: false });
    window.history.back();
  };

  handleDismiss = (): void => {
    this.recovery = initialRecoveryState;
    this.setState({ hasError: false, error: null, errorInfo: null, staleBundle: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full bg-card border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold">
                  {this.state.staleBundle ? "Ny versjon er tilgjengelig" : "Noe gikk galt"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {this.state.staleBundle
                    ? "Siden bruker en eldre versjon av todosystem. Last inn på nytt for å fortsette."
                    : "En uventet feil oppstod. Feilen er logget automatisk."}
                </p>
              </div>
            </div>
            {this.state.error && (
              <div className="p-3 rounded-md bg-muted text-sm font-mono overflow-auto max-h-32">
                {this.state.error.message}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {!this.state.staleBundle && (
                <Button
                  variant="outline"
                  onClick={this.handleGoBack}
                  data-testid="button-error-back"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Gå tilbake
                </Button>
              )}
              <Button 
                onClick={this.handleReset}
                data-testid="button-error-retry"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {this.state.staleBundle ? "Last inn ny versjon" : "Last inn på nytt"}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
